import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import { useAuth } from '../contexts/AuthContext';
import { PATH_AUTH, dashboardFor } from './paths';
import type { Role } from '../types/portal';

// ----------------------------------------------------------------------

/** Sends signed-out visitors to the login page, remembering where they were. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isInitialising } = useAuth();
  const location = useLocation();

  if (isInitialising) return <LoadingScreen />;

  if (!isAuthenticated) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`${PATH_AUTH.login}?next=${next}`} replace />;
  }

  return <>{children}</>;
}

/** Keeps employer pages away from job seekers and the other way round. */
export function RequireRole({ role, children }: { role: Role; children: ReactNode }) {
  const { account, isInitialising } = useAuth();

  if (isInitialising) return <LoadingScreen />;

  if (account && account.role !== role) {
    return (
      <Navigate
        to={dashboardFor(account.role)}
        replace
      />
    );
  }

  return <>{children}</>;
}

/** `/dashboard` itself just forwards to whichever side the account belongs to. */
export function DashboardIndexRedirect() {
  const { account, isInitialising } = useAuth();

  if (isInitialising) return <LoadingScreen />;

  return (
    <Navigate
      to={dashboardFor(account?.role)}
      replace
    />
  );
}
